import { StaticImage } from "gatsby-plugin-image"
import { Children, useEffect, useState } from "react"
import { Form as FormContainer } from "react-final-form"

import { Button, Heading, Icon } from "@trueskin-web/components"
import { arrowLongRightIcon } from "@trueskin-web/theme/icons/arrowLongRightIcon"
import { Trans } from "@trueskin-web/translations"

import Form from "./form"

const pushHistoryState = (query, value) => {
  const queryString = `?${query}=${value}`

  window.history.pushState(
    null,
    "",
    `${window.location.origin}${window.location.pathname}${queryString}`
  )
}

const WizardForm = ({
  initialValues,
  onContinue,
  onError,
  onSubmit,
  onPageLeave,
  children,
}) => {
  const searchParams = new URLSearchParams(window.location.search)
  const urlPage = searchParams.has("page")
    ? Number.parseInt(searchParams.get("page"))
    : 1

  const [currentPage, setCurrentPage] = useState(urlPage)

  const handleNext = () => {
    const nextPage = Math.min(currentPage + 1, children.length)

    setCurrentPage(nextPage)
    pushHistoryState("page", nextPage)
    onContinue()
  }

  const handlePrevious = () => {
    const prevPage = Math.max(currentPage - 1, 1)

    setCurrentPage(prevPage)
    pushHistoryState("page", prevPage)
    onContinue()
  }

  const handleSubmit = (values) => {
    if (isLastPage) {
      return onSubmit(values)
    }
    handleNext()
  }

  useEffect(() => {
    setCurrentPage(urlPage)
  }, [urlPage, setCurrentPage])

  const activePage = Children.toArray(children)[currentPage - 1]
  const isLastPage = currentPage === Children.count(children)
  const activePageTitle = activePage.props.title

  return (
    <div
      data-testid="web-portal-shipping-form"
      sx={{ mt: 5, maxWidth: [null, "686px"] }}
    >
      {activePageTitle && (
        <Heading mb={2} sx={{ fontFamily: "display", lineHeight: "body" }}>
          {activePageTitle}
        </Heading>
      )}
      <div sx={{ display: "flex", alignItems: "center", mb: 5, fontSize: 0 }}>
        <StaticImage
          src="../../../../images/products.png"
          alt=""
          objectFit="contain"
          height={32}
          sx={{ mr: 3 }}
        />
        <Trans i18nKey="Start.Shipping.WizardForm.description" />
      </div>

      <FormContainer
        keepDirtyOnReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({
          handleSubmit,
          submitting,
          values,
          errors,
          hasValidationErrors,
        }) => (
          <Form
            onSubmit={(event) => {
              if (hasValidationErrors) {
                onError(errors, values)
              }
              handleSubmit(event)
            }}
            onPageLeave={onPageLeave}
          >
            {activePage}

            <Button
              data-testid={
                isLastPage
                  ? "web-portal-shipping-save-btn"
                  : "web-portal-shipping-continue-btn"
              }
              type="submit"
              isLoading={submitting}
              size="sm"
              rounded
              sx={{ gridColumnStart: [null, 1], gap: 1 }}
            >
              {isLastPage ? (
                <Trans i18nKey="Start.Shipping.proceedToCheckout" />
              ) : (
                <Trans i18nKey="Start.Shipping.nextPageButton" />
              )}
              <Icon icon={arrowLongRightIcon} sx={{ mt: "2px" }} />
            </Button>

            {currentPage > 1 && (
              <Button
                variant="link"
                size="sm"
                onClick={handlePrevious}
                sx={{
                  gridColumnStart: [null, 1],
                  justifyContent: [null, "flex-start"],
                  gap: 1,
                  px: [null, 0],
                }}
              >
                <Trans i18nKey="Start.Shipping.previousPageButton" />
              </Button>
            )}
          </Form>
        )}
      </FormContainer>
    </div>
  )
}

WizardForm.Page = ({ children }) => children

export default WizardForm
